import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_SEO, SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, getSeoForPath } from '../seoData';
import { useSeoData } from '../contexts/SeoDataContext';
import { trackEvent } from '../utils/analytics';

// Auto-resolves title/description/keywords/canonical/OG/Twitter tags from
// the backend-driven seo_pages/seo_settings data (via SeoDataContext), based
// on the current route, so individual page components don't each need their
// own <Helmet> block. Pages with genuinely unique content (e.g. a specific
// event) can still override via props. Falls back to the hardcoded
// seoData.js defaults if the API hasn't loaded yet or failed.
// Admins sometimes paste the full "google-site-verification=CODE" string
// (that's the format Google shows for the DNS TXT record method) into the
// verification ID field instead of just the code, which the HTML tag method
// actually needs. Strip the prefix so either input format produces a valid
// <meta content="CODE"> tag.
function extractGoogleVerificationCode(value) {
  if (!value) return null;
  return value.replace(/^google-site-verification=/, '').trim() || null;
}

export default function Seo({ title, description, keywords, image, noindex }) {
  const { pathname } = useLocation();
  const { pagesByPath, settings } = useSeoData();
  const matched = getSeoForPath(pathname, pagesByPath);

  const finalTitle = title || matched.title || DEFAULT_SEO.title;
  const finalDescription = description || matched.description || settings?.meta_description || DEFAULT_SEO.description;
  const finalKeywords = keywords || matched.keywords || settings?.meta_keywords || DEFAULT_SEO.keywords;
  const finalImage = image || matched.ogImage || settings?.og_image || DEFAULT_OG_IMAGE;
  const finalNoindex = noindex ?? matched.noindex ?? DEFAULT_SEO.noindex;
  const canonicalUrl = `${SITE_URL}${pathname}`;
  const googleVerificationCode = extractGoogleVerificationCode(settings?.google_console_id);

  // This is a React Router SPA — client-side navigation never triggers a
  // full page load, so GTM's default "Page View" trigger (which only fires
  // once, when the container script first loads) never sees any of the
  // in-app navigation that follows. Without this, GA4 only ever counts the
  // single landing page per visit no matter how many pages someone actually
  // browses. Pushing a page_view on every route change gives GTM something
  // to listen for — the GA4 tag inside GTM needs its trigger changed from
  // "All Pages" to a Custom Event trigger matching event name "page_view"
  // (or use GTM's built-in "History Change" trigger type instead).
  useEffect(() => {
    trackEvent('page_view', {
      page_path: pathname,
      page_location: window.location.href,
      page_title: finalTitle,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Helmet htmlAttributes={{ lang: 'en-US' }}>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={finalNoindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />
      {googleVerificationCode && (
        <meta name="google-site-verification" content={googleVerificationCode} />
      )}

      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={canonicalUrl} />
      {settings?.facebook_url && <meta property="og:see_also" content={settings.facebook_url} />}
      {settings?.linkedin_url && <meta property="og:see_also" content={settings.linkedin_url} />}

      <meta name="twitter:card" content="summary_large_image" />
      {settings?.twitter_handle && <meta name="twitter:site" content={settings.twitter_handle} />}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
    </Helmet>
  );
}
